import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Brain, 
  Building2, 
  CreditCard, 
  Shield, 
  ArrowRight,
  Mic,
  Route,
  Zap,
  Users,
  FileCheck,
  DollarSign,
  Clock,
  Camera,
  FileText,
  Wallet
} from 'lucide-react';

const ecosystemPillars = [
  {
    id: 'dispatch',
    title: 'AI Dispatch',
    subtitle: 'OVA Engine',
    icon: Brain,
    color: 'cyan',
    features: [
      { icon: Mic, label: 'Conversational AI Dispatch' },
      { icon: Route, label: 'Real-time Trip Matching' },
      { icon: Zap, label: 'Intelligent Routing' },
    ],
    description: 'Replace outdated dispatch systems with conversational AI that understands context, handles multi-leg trips, and optimizes in real-time.',
    gradient: 'from-cyan-500/20 to-blue-500/20',
    borderColor: 'border-neon-cyan/30',
    glowColor: 'shadow-glow-cyan',
  },
  {
    id: 'nemt',
    title: 'NEMT Infrastructure',
    subtitle: 'Medex Network',
    icon: Building2,
    color: 'purple',
    features: [
      { icon: Users, label: 'Direct Trip Facilitation' },
      { icon: Route, label: 'Nationwide Driver Network' },
      { icon: FileCheck, label: 'Trip Lifecycle Automation' },
    ],
    description: 'Not a broker — a direct facilitator. Work alongside brokers to provide services to providers and independent driver networks with the highest quality of care.',
    gradient: 'from-purple-500/20 to-pink-500/20',
    borderColor: 'border-neon-purple/30',
    glowColor: 'shadow-glow-purple',
  },
  {
    id: 'finance',
    title: 'Financial Infrastructure',
    subtitle: 'Medex Pay',
    icon: CreditCard,
    color: 'blue',
    features: [
      { icon: DollarSign, label: 'Payment Processing for Providers' },
      { icon: Clock, label: 'Instant Payouts for Drivers' },
      { icon: Wallet, label: 'Revenue Optimization Tools' },
    ],
    description: 'Unified financial layer with 837 and CMS1500 integrations. Process payments, manage billing, and optimize cash flow across the entire network.',
    gradient: 'from-blue-500/20 to-indigo-500/20',
    borderColor: 'border-neon-blue/30',
    glowColor: 'shadow-glow-blue',
  },
  {
    id: 'compliance',
    title: 'Risk + Compliance',
    subtitle: 'Protection Layer',
    icon: Shield,
    color: 'green',
    features: [
      { icon: Camera, label: 'NEMT Vision AI Dash Cameras' },
      { icon: Shield, label: 'Medex Group Insurance Plan' },
      { icon: FileText, label: 'Centralized Credentialing' },
    ],
    description: 'Complete risk management with AI-powered monitoring, group insurance coverage, and automated compliance tracking across all operations.',
    gradient: 'from-green-500/20 to-emerald-500/20',
    borderColor: 'border-neon-green/30',
    glowColor: 'shadow-[0_0_20px_-5px_rgba(16,185,129,0.4)]',
  },
];

export const EcosystemSection = () => {
  const [activePillar, setActivePillar] = useState(null);

  return (
    <section 
      id="ecosystem" 
      className="relative py-24 lg:py-32 overflow-hidden"
      data-testid="ecosystem-section"
    >
      {/* Background */}
      <div className="absolute inset-0 neural-bg opacity-30" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1 rounded-full bg-white/5 border border-white/10 text-sm font-mono text-slate-400 uppercase tracking-wider mb-6">
            The Ecosystem
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            MedexOVA Is More Than Rides
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-slate-400">
            Four pillars working together to create a unified AI-driven mobility and healthcare logistics ecosystem.
          </p>
        </motion.div>

        {/* Pillars Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {ecosystemPillars.map((pillar, index) => (
            <motion.div
              key={pillar.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`
                relative group cursor-pointer
                glass-card p-6 lg:p-8
                hover:${pillar.borderColor} 
                transition-all duration-500
                ${activePillar === pillar.id ? pillar.borderColor + ' ' + pillar.glowColor : ''}
              `}
              onClick={() => setActivePillar(activePillar === pillar.id ? null : pillar.id)}
              data-testid={`ecosystem-pillar-${pillar.id}`}
            >
              {/* Background gradient on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${pillar.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`} />
              
              <div className="relative z-10">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`p-2 rounded-lg bg-${pillar.color === 'cyan' ? 'neon-cyan' : pillar.color === 'purple' ? 'neon-purple' : pillar.color === 'blue' ? 'neon-blue' : 'neon-green'}/10`}>
                        <pillar.icon className={`w-6 h-6 text-${pillar.color === 'cyan' ? 'neon-cyan' : pillar.color === 'purple' ? 'neon-purple' : pillar.color === 'blue' ? 'neon-blue' : 'neon-green'}`} />
                      </div>
                      <span className="text-xs font-mono text-slate-500 uppercase tracking-wider">
                        {pillar.subtitle}
                      </span>
                    </div>
                    <h3 className="text-xl lg:text-2xl font-heading font-bold text-white">
                      {pillar.title}
                    </h3>
                  </div>
                  <ArrowRight className={`w-5 h-5 text-slate-500 group-hover:text-white transform transition-all duration-300 ${activePillar === pillar.id ? 'rotate-90' : 'group-hover:translate-x-1'}`} />
                </div>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  {pillar.features.map((feature, i) => (
                    <div key={i} className="flex items-center space-x-3 text-slate-400">
                      <feature.icon className="w-4 h-4 text-slate-500" />
                      <span className="text-sm">{feature.label}</span>
                    </div>
                  ))}
                </div>

                {/* Expanded Description */}
                <motion.div
                  initial={false}
                  animate={{ 
                    height: activePillar === pillar.id ? 'auto' : 0,
                    opacity: activePillar === pillar.id ? 1 : 0 
                  }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-slate-300 leading-relaxed">
                      {pillar.description}
                    </p>
                  </div>
                </motion.div>

                {/* Click indicator */}
                <div className="mt-4 text-xs font-mono text-slate-600">
                  {activePillar === pillar.id ? 'Click to collapse' : 'Click to expand'}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Connection visualization */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center justify-center space-x-4">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-neon-cyan/50" />
            <span className="text-sm font-mono text-slate-500 uppercase tracking-wider">
              Unified by AI
            </span>
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-neon-purple/50" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default EcosystemSection;
